/**
 * What the game sounds like, as a function of what Rosalie is doing.
 *
 * `engine.ts` is the plumbing — contexts, buses, panners, voices. This module is
 * the decision layer on top of it, and it is separate for the same reason
 * `palette.ts` is separate from the shader: the mapping from state to sound is
 * art direction, and art direction that lives inside a system gets edited by
 * accident.
 *
 * Until this existed the game was completely silent. Seven sounds passed the
 * offline gate and nothing ever played one, which is the least useful place for
 * a sound to be.
 */

import { AudioEngine, type AudioHandle, type Vec3 } from '@/audio/engine';
import { getSound } from '@/audio/registry';
import { FOOTFALL_SOUND, GAIT_EMISSION_MULTIPLIER, type SurfaceKind } from '@/audio/sounds/foley';
import type { Gait } from '@/physics/character';

/**
 * Everything the mix reads. Deliberately a plain snapshot rather than the
 * controller itself — this module must not be able to influence the simulation,
 * only observe it.
 */
export interface AudioFrame {
  gait: Gait;
  speed: number;
  grounded: boolean;
  /** Non-zero on the step she lands, already latched by the caller. */
  landingImpact: number;
  /** 0 when not scrambling, otherwise eased progress 0..1. */
  scramble: number;
  /**
   * The animation player's TOTAL locomotion cycles, unwrapped and monotonic.
   *
   * Deliberately not the wrapped phase. From a wrapped value you cannot tell
   * "advanced a little" from "advanced a whole cycle and a little", so a long
   * frame silently dropped a footfall — which is the failure this trigger was
   * rewritten to prevent in the first place, and it survived the rewrite.
   */
  cycles: number;
  /**
   * Metres she travels per roll revolution — the same figure `main.ts` spins her
   * body by. Passed in rather than duplicated here so the sound and the visible
   * rotation cannot disagree.
   */
  rollCircumference: number;
  position: Vec3;
}

/** Every sound the game asks for, so they can be rendered before they are needed. */
export const REQUIRED_SOUNDS: readonly string[] = [
  'hall-hum-40',
  'extractor-fan',
  'fluorescent-buzz',
  'slurry-pump',
  'rosalie-breath-calm',
  'rosalie-breath-alert',
  'rosalie-breath-ragged',
  'footfall-concrete',
  'footfall-straw',
  'footfall-grating',
  'landing-thud',
  'roll-rumble',
  'scramble-scrabble',
  // Beat 4's dog and its handler. Prewarmed with everything else and not lazily
  // on the first bark of the corridor, because `AudioEngine.playOneShot` returns
  // silence for an id it has not rendered yet — so the first claw tick, which is
  // the one that tells the player there is a dog at all, would be the one that
  // never played.
  'dog-pant',
  'dog-claws-concrete',
  'dog-snuff',
  'handler-whistle',
  // Beat 10's file must be warm before the player creates a gap. The stall is
  // the only explanation that rolling has left the animals outside draw range;
  // dropping its first one-shot would leave the mechanic silent again.
  'herd-shuffle',
  'herd-stall',
  // Beat 1's voices (D-250). The sow's grunt answers the very first verb the
  // game teaches, seconds after boot; a lazily rendered first grunt would be
  // the tutorial answering with silence, which is what it did before it had a
  // voice at all.
  'sow-grunt',
  'piglet-squeak',
  'piglet-squeal',
  // Beat 5's plant. Same argument as the dog's four above, and it bites harder
  // here: `plant-cycle` is the room's clock and the first thump is the one that
  // establishes there is a machine at all. A lazily rendered first cycle would
  // be the one nobody heard, in the beat whose whole pulse it is.
  'plant-cycle',
  'sheeting-rustle',
  // Beat 6's weather and the yard's floor tone, prewarmed for the same reason
  // and one more: `rain-bed` is the first thing the beat plays, under a fade
  // that is still black, and a bed that arrives four seconds late would arrive
  // after the shot it is establishing.
  'rain-bed',
  'rain-on-steel',
  'thunder-near',
  'thunder-far',
  'truck-idle',
  'race-clang',
  'yard-footfall',
  'bin-castors',
  'bin-lid-close',
  // Beat 8's fuse is an event, not a generic footfall. It must be warm before
  // the player reaches the box or the one clack that explains the blackout is
  // the one one-shot the engine deliberately drops.
  'fuse-throw',
  'hens-panic',
  // Holly fires 1.85 strokes a second the instant she follows. A lazily rendered
  // first stroke would drop the one that teaches the player she makes a sound.
  'holly-bell',
];

/** One spatialised machine, somewhere in a room. */
export interface AmbienceSource {
  id: string;
  at: Vec3;
  gain: number;
}

/**
 * Where the machinery sits, when nobody has said.
 *
 * These are TEST PLACEMENTS for the grey-box room (D-023), not level design:
 * three spatialised sources far apart in a 40 m box, which is what it takes to
 * hear whether panning and distance attenuation work at all.
 *
 * **A real room supplies its own** through `setAmbience()`. It has to — these
 * coordinates are metres outside the walls of the farrowing hall, so leaving
 * them in place there put the extractor fan in the car park. Found the moment
 * the hall replaced the grey box as the room the game boots.
 *
 * The hall hum is deliberately NOT in this list. It is the building, not an
 * object in it, so it plays without a panner — walking away from a room's own
 * tone is not a thing you can do.
 */
const DEFAULT_AMBIENCE: readonly AmbienceSource[] = [
  { id: 'extractor-fan', at: { x: -16, y: 2.6, z: -16 }, gain: 0.9 },
  { id: 'fluorescent-buzz', at: { x: 2, y: 2.8, z: 8 }, gain: 0.7 },
  { id: 'slurry-pump', at: { x: 14, y: 0.3, z: -10 }, gain: 0.8 },
];

/**
 * Gait → footfall level, tracking `GAIT_EMISSION_MULTIPLIER` rather than being
 * tuned by ear against it.
 *
 * The audio direction is explicit that footfall loudness IS the stealth system,
 * and that a surface which sounds quiet but emits loudly breaks the fairness
 * rule the whole design rests on. Deriving one from the other means the two
 * cannot drift: change the emission ladder and the mix follows.
 */
function footfallGain(gait: Gait): number {
  const multiplier = gait === 'creep' ? GAIT_EMISSION_MULTIPLIER.creep
    : gait === 'trot' ? GAIT_EMISSION_MULTIPLIER.trot
    : GAIT_EMISSION_MULTIPLIER.walk;
  return 0.85 * multiplier;
}

/** Her exertion, 0..1 — the only thing driving breath rate before stealth exists. */
function exertion(frame: AudioFrame): number {
  if (frame.gait === 'roll') return 1;
  if (frame.gait === 'trot') return 0.55 + Math.min(0.35, frame.speed * 0.1);
  if (frame.gait === 'walk') return 0.3;
  if (frame.gait === 'creep') return 0.22;
  return 0.08;
}

/**
 * How far her breathing may pull the rest of the mix down, at full tension.
 *
 * A third, not a half. The farm never falling silent is the point of the beds —
 * duck them far enough and the room stops existing, which loses more than the
 * breathing gains. Revisit when stealth drives tension, because a held breath
 * while hiding is the case this number really exists for.
 */
const MAX_BREATH_DUCK = 0.35;

/**
 * Peak level of a breath layer.
 *
 * Was 0.9 and came back from review as "too loud" — while also being "actually
 * very well recognizable", which is the useful half of that note. The sound is
 * right; there was simply too much of it. Halved rather than trimmed, because a
 * bed you notice is a bed that will wear out over ninety minutes, and D-009
 * needs this one audible for the entire game.
 */
const BREATH_GAIN = 0.45;

/**
 * The three breath renders, and where on the tension axis each one sits.
 *
 * D-009 builds breathing as ONE generator over a `tension` parameter,
 * specifically so the player can read rate of change; three clips snapped
 * between would destroy exactly that. The registry holds three offline renders
 * of that generator, so the runtime restores the continuity by crossfading them
 * — an approximation, but one that preserves the property the decision is about.
 */
/**
 * Bounds on the rumble's playback rate.
 *
 * Locking rate to real rotation is right until she is barely moving, where the
 * honest rate approaches zero and the loop turns into a drone of stretched
 * artefacts. The floor keeps it recognisable; the ceiling stops a downhill run
 * turning it into a whine.
 */
const MIN_ROLL_RATE = 0.55;
const MAX_ROLL_RATE = 1.9;

const BREATH_LAYERS: readonly { id: string; at: number }[] = [
  { id: 'rosalie-breath-calm', at: 0 },
  { id: 'rosalie-breath-alert', at: 0.55 },
  { id: 'rosalie-breath-ragged', at: 1 },
];

export class GameAudio {
  readonly engine: AudioEngine;

  #started = false;
  #starting = false;
  #muted = false;
  #beds: AudioHandle[] = [];
  /** The loop ids still contributing to the room. Kept beside handles so a
   * blackout can be tested as the audible state it changes, not inferred from
   * a visual cue or a voice count that also includes Rosalie's breathing. */
  #activeBedIds: string[] = [];
  #ambience: readonly AmbienceSource[] = DEFAULT_AMBIENCE;
  #breath: { handle: AudioHandle; at: number }[] = [];
  #roll: AudioHandle | undefined;
  #wasScrambling = false;
  #lastCycles = 0;
  #footfallArmed = false;
  #tension = 0;
  /** One revolution of `roll-rumble`, seconds. Read from the registry, not guessed. */
  #rollLoopSeconds = 1;
  /**
   * Which floor she is on. One value for now, because the grey-box room is one
   * material — the point of routing it through `SurfaceKind` anyway is that the
   * seam for straw and grating is already here when the hall is dressed.
   */
  surface: SurfaceKind = 'concrete';

  constructor(engine: AudioEngine = new AudioEngine({ masterGain: 0.75, observeVisibility: true })) {
    this.engine = engine;
  }

  get muted(): boolean {
    return this.#muted;
  }

  /**
   * The machinery in the room she is standing in.
   *
   * **Must be called before `start()`.** The beds are acquired once, at start,
   * and never re-acquired — see the comment there about loop handles. Calling
   * this afterwards is not an error and simply has no effect until the next
   * start, which is the honest behaviour for a zone change that has not been
   * built yet.
   */
  setAmbience(sources: readonly AmbienceSource[]): void {
    this.#ambience = sources;
  }

  /** True once a user gesture has brought the mix up. */
  get started(): boolean {
    return this.#started;
  }

  /** The room machinery currently in the mix. Diagnostics only. */
  get activeBedIds(): readonly string[] {
    return this.#activeBedIds;
  }

  /**
   * Cut this room's machinery while leaving breath, footfalls and one-shots alive.
   *
   * The fuse is not a mute button: Rosalie still moves and the crate still has
   * to sound. It is specifically the hall's mains-fed hum, extractor and pump
   * stopping under the lever. Handles rather than a category-bus duck make that
   * distinction structural, and the short ramp meets the clack without a click.
   */
  cutAmbience(fadeSeconds = 0.045): void {
    for (const bed of this.#beds) bed.stop(fadeSeconds);
    this.#beds = [];
    this.#activeBedIds = [];
  }

  /**
   * Bring the mix up. Must be called from a real user gesture — the game uses
   * the click that captures the pointer, which every player performs anyway.
   *
   * Prewarming before the first bed starts is not an optimisation. Rendering is
   * async, so an un-prewarmed one-shot is simply dropped, and the ones that
   * would be dropped are her first footfall and her first landing.
   */
  async start(): Promise<void> {
    // Two flags, not one. `#starting` makes a second click a no-op; `#started`
    // is what `update()` gates on, and it must not go true until the buffers are
    // actually there. With a single flag there is a window — unlock and prewarm
    // are awaited — in which the mix is "started" and every buffer is still
    // rendering. A roll entered in that window takes a silent handle and holds
    // it for the whole roll, because loops are acquired once and never retried.
    if (this.#starting) return;
    this.#starting = true;
    await this.engine.unlock();
    await this.engine.prewarm(REQUIRED_SOUNDS);
    this.#started = true;

    // Read, not hardcoded: the rumble's loop is one revolution, and the rate that
    // locks it to her visible spin depends on how long that revolution is. A
    // constant here would silently desynchronise the moment the sound is retuned.
    const rumble = getSound('roll-rumble');
    if (rumble && rumble.duration > 0) this.#rollLoopSeconds = rumble.duration;

    // Started silent and ramped, so the farm fades up rather than switching on.
    // A bed that appears instantly announces itself as a bed.
    const hum = this.engine.playLoop('hall-hum-40', { gain: 0 });
    hum.setGain(0.85, 2.5);
    this.#beds.push(hum);
    this.#activeBedIds.push('hall-hum-40');
    for (const source of this.#ambience) {
      const voice = this.engine.playLoop(source.id, { gain: 0, position: source.at });
      voice.setGain(source.gain, 2.5);
      this.#beds.push(voice);
      this.#activeBedIds.push(source.id);
    }

    // All three breath layers run continuously and are mixed by gain. Starting
    // and stopping them per state would reintroduce the snapping the crossfade
    // exists to avoid, and three looping buffers cost nothing.
    //
    // Non-spatial, and that is a decision rather than an omission: her breathing
    // is not a sound in the room, it is the tension meter D-009 uses INSTEAD of
    // a HUD. A meter that gets quieter when the camera pulls back is not a meter.
    for (const layer of BREATH_LAYERS) {
      this.#breath.push({ handle: this.engine.playLoop(layer.id, { gain: 0 }), at: layer.at });
    }

    this.#applyMasterGain(0.6);
  }

  /** Toggle silence. Returns the new state so the caller can persist it. */
  toggleMute(): boolean {
    this.#muted = !this.#muted;
    this.#applyMasterGain(0.25);
    return this.#muted;
  }

  /**
   * Hand the engine the list of sounds it must not start. Gentle Mode's mute
   * half; the list itself is authored in `src/shell/gentle.ts`.
   */
  setSuppressedSounds(ids: readonly string[]): void {
    this.engine.setSuppressed(ids);
  }

  setMuted(muted: boolean): void {
    if (muted === this.#muted) return;
    this.#muted = muted;
    this.#applyMasterGain(0.25);
  }

  /** Pause owns the whole mix, using the engine's visibility-safe suspension path. */
  setPaused(paused: boolean): Promise<void> {
    return this.engine.setPaused(paused);
  }

  /** Called once per rendered frame, after animation has advanced. */
  update(dt: number, frame: AudioFrame, listener: { position: Vec3; forward: Vec3; up: Vec3 }): void {
    if (!this.#started) return;
    this.engine.setListener(listener.position, listener.forward, listener.up);

    this.#footfalls(frame);
    this.#landing(frame);
    this.#scramble(frame);
    this.#rollRumble(frame);
    this.#breathe(frame, dt);
  }

  dispose(): void {
    this.engine.dispose();
  }

  #applyMasterGain(rampSeconds: number): void {
    this.engine.setMasterGain(this.#muted ? 0 : 0.75, rampSeconds);
  }

  /**
   * Footfalls, fired off the animation phase rather than a timer.
   *
   * `AnimationPlayer` derives locomotion phase from DISTANCE TRAVELLED, not from
   * a clock — that is what stops her feet sliding. Triggering the sound from the
   * same number inherits the property for free: the footfall lands on the frame
   * the foot does, at any speed, up a slope, out of a turn, with nothing to tune
   * separately and nothing that can drift out of sync later.
   *
   * TWO per cycle, because `sampleGait` animates diagonal pairs — FL+BR, then
   * FR+BL half a cycle behind. A real pig walking gives four beats, and using
   * four here would be more correct and would sound wrong: two of them would
   * have no visible foot plant under them. The animation is the authority.
   */
  #footfalls(frame: AudioFrame): void {
    const walking = frame.gait === 'creep' || frame.gait === 'walk' || frame.gait === 'trot';
    if (!walking || !frame.grounded) {
      // Disarmed, because `phase` means something else while she is idle — the
      // player reports the idle clip's own phase there. Comparing across that
      // boundary invents a crossing and fires a step she never took.
      this.#footfallArmed = false;
      return;
    }

    const cycles = frame.cycles;
    const previous = this.#lastCycles;
    this.#lastCycles = cycles;
    if (!this.#footfallArmed) {
      this.#footfallArmed = true;
      return;
    }

    // Two plants per cycle, counted off a monotonic total.
    //
    // This is arithmetic on an unwrapped number precisely so there is nothing to
    // get wrong. Two previous versions did it on the wrapped phase: the first
    // returned a boolean and dropped a step whenever a frame spanned both plants,
    // and the second still missed a frame that spanned a whole cycle, because a
    // wrapped value cannot distinguish that from no movement at all. Both were
    // found by review rather than by playing, since the symptom is a limp.
    const plants = Math.min(Math.floor(cycles * 2) - Math.floor(previous * 2), 2);
    if (plants <= 0) return;

    for (let i = 0; i < plants; i++) {
      this.engine.playOneShot(FOOTFALL_SOUND[this.surface], {
        gain: footfallGain(frame.gait),
        position: frame.position,
        // Real feet are not identical. Without this the loop becomes a machine
        // gun after four steps, which is the single most common way footstep
        // audio gives itself away as a sample.
        detune: 90,
      });
    }
  }

  #landing(frame: AudioFrame): void {
    if (frame.landingImpact <= 0) return;
    this.engine.playOneShot('landing-thud', {
      // Scaled by impact so a step down and a real drop are not the same event.
      gain: 0.5 + Math.min(0.6, frame.landingImpact * 0.5),
      position: frame.position,
      detune: 60,
    });
  }

  #scramble(frame: AudioFrame): void {
    const scrambling = frame.scramble > 0;
    if (scrambling && !this.#wasScrambling) {
      this.engine.playOneShot('scramble-scrabble', { gain: 0.9, position: frame.position, detune: 70 });
    }
    this.#wasScrambling = scrambling;
  }

  /**
   * The roll is one continuous voice whose rate and level follow her speed.
   *
   * Same reasoning as the spin in `main.ts`: the rotation is locked to distance
   * travelled so it cannot look like skidding, and the sound is locked to speed
   * so it cannot sound like it. It fades rather than cuts on exit, because she
   * decelerates out of a roll rather than stopping dead.
   */
  #rollRumble(frame: AudioFrame): void {
    if (frame.gait === 'roll') {
      // `!active` covers the handle that came back inert — the engine returns a
      // silent no-op handle whenever the context is not running, which happens
      // if a roll begins while the tab is hidden. Loops are acquired once and
      // held, so without this re-check that roll stays silent for its whole
      // duration even after the tab comes back. Raised on independent review.
      if (!this.#roll || !this.#roll.active) {
        this.#roll = this.engine.playLoop('roll-rumble', { gain: 0, position: frame.position });
      }
      const intensity = Math.min(1, frame.speed / 3);
      this.#roll.setPosition(frame.position);
      this.#roll.setGain(0.35 + intensity * 0.6, 0.08);

      // Rate LOCKED to her actual rotation, not guessed from speed.
      //
      // The rumble's loop is one revolution long and contains her body's contacts
      // with the floor, so playing it at the rate she is actually turning puts
      // those contacts under the rotation you can see — the same trick the
      // footfalls use, and the same reason: a sound derived from the thing on
      // screen cannot drift from it.
      //
      // `main.ts` advances the visible spin by speed/ROLL_CIRCUMFERENCE
      // revolutions per second, so that is the number the loop has to match.
      const revolutionsPerSecond = frame.speed / frame.rollCircumference;
      const rate = revolutionsPerSecond * this.#rollLoopSeconds;
      this.#roll.setPlaybackRate(Math.max(MIN_ROLL_RATE, Math.min(MAX_ROLL_RATE, rate)), 0.08);
    } else if (this.#roll) {
      this.#roll.stop(0.25);
      this.#roll = undefined;
    }
  }

  /** Crossfade the breath layers toward her exertion. */
  #breathe(frame: AudioFrame, dt: number): void {
    const target = exertion(frame);
    // Smoothed, because the READ is the rate of change (D-009). A gait change is
    // instant and a body's breathing is not; snapping here would report the state
    // machine rather than the animal.
    this.#tension += (target - this.#tension) * Math.min(1, dt * 1.6);

    for (const layer of this.#breath) {
      // Triangular blend: full at its own point on the axis, silent one step away.
      const distance = Math.abs(this.#tension - layer.at);
      const weight = Math.max(0, 1 - distance / 0.55);
      layer.handle.setGain(weight * BREATH_GAIN, 0.1);
    }

    // Her breathing ducks everything else, never the reverse.
    //
    // `docs/audio-direction.md` states this as a contract and the engine has had
    // `duck()` since it was written, but nothing called it — the doc promised a
    // behaviour the code did not have, which is worse than not promising it.
    // Found on independent review.
    //
    // Proportional to tension rather than switched, for the same reason the
    // breath layers crossfade: the player reads rate of change (D-009). The
    // ceiling is deliberately modest — this is meant to make room for her, not
    // to drop the farm out from under her.
    this.engine.duck(this.#tension * MAX_BREATH_DUCK);
  }
}
